import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { OfficialApiInbox } from '../types/template';
import { BusinessProfileModal } from './BusinessProfileModal';
import { useProfilePictures } from '../hooks/useProfilePictures';

interface TemplateInboxListProps {
  inboxes: OfficialApiInbox[];
  onSelectInbox: (inbox: OfficialApiInbox) => void;
  onRefresh: () => void;
}

export function TemplateInboxList({ inboxes, onSelectInbox, onRefresh }: TemplateInboxListProps) {
  const { theme } = useTheme();
  const [profileInbox, setProfileInbox] = useState<OfficialApiInbox | null>(null);
  
  // Hook para carregar fotos de perfil
  const { 
    getProfilePicture, 
    hasProfilePicture, 
    isLoadingProfilePicture,
    isAccountBlocked,
    loading: loadingPictures 
  } = useProfilePictures(inboxes);

  const formatPhoneNumber = (phone: string) => {
    // Formatar número brasileiro: +55 (11) 99999-9999
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('55') && cleaned.length === 13) {
      return `+55 (${cleaned.substr(2, 2)}) ${cleaned.substr(4, 5)}-${cleaned.substr(9, 4)}`;
    }
    return phone;
  };

  if (inboxes.length === 0) {
    return (
      <div className={`rounded-2xl p-12 text-center ${
        theme === 'dark' ? 'bg-[#2A2A2A]' : 'bg-white'
      } shadow-sm`}>
        <div className="max-w-lg mx-auto">
          {/* WhatsApp icon */}
          <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{
            backgroundColor: theme === 'dark' ? 'rgba(37, 211, 102, 0.1)' : 'rgba(37, 211, 102, 0.08)'
          }}>
            <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#25D366"/>
              <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="#25D366" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>

          <h3 className={`text-xl font-bold mb-3 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Nenhuma caixa de entrada da API Oficial conectada
          </h3>
          
          <p className={`mb-2 text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Para usar Templates, você precisa ter uma caixa de entrada da <strong>API Oficial do WhatsApp</strong> (Cloud API) conectada à sua conta.
          </p>
          
          <div className={`rounded-lg p-4 mb-6 text-left ${
            theme === 'dark' ? 'bg-yellow-900/15 border border-yellow-800/30' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-start gap-3">
              <span className="text-lg mt-0.5">💡</span>
              <div className={`text-sm ${
                theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
              }`}>
                <p className="font-medium mb-1">Como conectar:</p>
                <p>Acesse as <strong>Configurações</strong> da plataforma e adicione uma caixa de entrada do tipo <strong>WhatsApp Cloud API</strong> com as credenciais do seu número verificado na Meta.</p>
              </div>
            </div>
          </div>

          <button
            onClick={onRefresh}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            🔄 Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header com info */}
      <div className={`rounded-lg p-4 mb-6 ${
        theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
      }`}>
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className={`text-sm font-medium ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
            }`}>
              Selecione um número
            </p>
            <p className={`text-sm mt-1 ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-700'
            }`}>
              Clique em uma caixa de entrada para gerenciar seus templates da API Oficial do WhatsApp
            </p>
          </div>
          {loadingPictures && (
            <div className="flex items-center gap-2 text-xs">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>
                Carregando fotos de perfil...
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Grid de caixas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {inboxes.map((inbox) => {
          const phoneNumberId = inbox.providerConfig.phoneNumberId;
          const isBlocked = isAccountBlocked(phoneNumberId);
          
          return (
            <div
              key={inbox.id}
              className={`group relative rounded-xl p-6 transition-all duration-200 ${
                isBlocked
                  ? 'border-2 border-red-500 bg-red-50 dark:bg-red-900/10'
                  : 'bg-theme-card border border-theme-border shadow-sm'
              }`}
            >
            {/* Foto de Perfil ou Ícone */}
            <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-4 transition-colors overflow-hidden bg-blue-100 group-hover:bg-blue-200 dark:bg-blue-900/30 dark:group-hover:bg-blue-900/50">
              {(() => {
                const phoneNumberId = inbox.providerConfig.phoneNumberId;
                const profilePictureUrl = getProfilePicture(phoneNumberId);
                const isLoading = isLoadingProfilePicture(phoneNumberId);
                const hasPicture = hasProfilePicture(phoneNumberId);
                
                if (isLoading) {
                  // Mostrar loading spinner
                  return (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                  );
                } else if (hasPicture && profilePictureUrl) {
                  // Mostrar foto de perfil
                  return (
                    <img
                      src={profilePictureUrl}
                      alt={`Foto de perfil ${inbox.name}`}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        // Fallback para ícone se a imagem falhar ao carregar
                        console.warn(`⚠️ Erro ao carregar foto de perfil para ${inbox.name}`);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                            <svg class="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          `;
                        }
                      }}
                    />
                  );
                } else {
                  // Mostrar ícone de telefone (fallback)
                  return (
                    <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  );
                }
              })()}
            </div>

            {/* Nome */}
            <h3 className="text-lg font-semibold mb-2 text-theme-text">
              {inbox.name}
            </h3>

            {/* Número */}
            <p className="text-sm mb-3 text-theme-text-secondary">
              {formatPhoneNumber(inbox.phoneNumber)}
            </p>

            {/* Info adicional */}
            <div className="space-y-2">
              <div className="flex items-center gap-4 text-xs">
                <span className={`flex items-center gap-1 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  ID: {inbox.channelId}
                </span>
              </div>

              {/* Informações de Saúde do Número */}
              {inbox.status && inbox.qualityRating && (
                <div className={`flex items-center gap-3 pt-2 border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  {/* Status */}
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>Status:</span>
                    <span className={`font-medium ${
                      inbox.status === 'CONNECTED' 
                        ? theme === 'dark' ? 'text-green-400' : 'text-green-700'
                        : inbox.status === 'DISCONNECTED'
                        ? theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        : theme === 'dark' ? 'text-red-400' : 'text-red-600'
                    }`}>
                      {inbox.status === 'CONNECTED' ? 'Conectado' : inbox.status === 'DISCONNECTED' ? 'Desconectado' : inbox.status === 'FLAGGED' ? 'Sinalizado' : 'Desconhecido'}
                    </span>
                  </div>

                  {/* Qualidade ou Restrição */}
                  {isBlocked ? (
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                        Número com Restrição
                      </span>
                    </div>
                  ) : inbox.qualityRating !== 'UNKNOWN' ? (
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>Qualidade:</span>
                      <span className={`flex items-center gap-1 font-medium ${
                        inbox.qualityRating === 'GREEN' 
                          ? theme === 'dark' ? 'text-green-400' : 'text-green-700'
                          : inbox.qualityRating === 'YELLOW'
                          ? theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                          : theme === 'dark' ? 'text-red-400' : 'text-red-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          inbox.qualityRating === 'GREEN' ? 'bg-green-500' : inbox.qualityRating === 'YELLOW' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></span>
                        {inbox.qualityRating === 'GREEN' ? 'Alta' : inbox.qualityRating === 'YELLOW' ? 'Média' : 'Baixa'}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Botões de ação */}
            <div className="mt-4 pt-4 border-t flex gap-2">
              <button
                onClick={() => !isBlocked && onSelectInbox(inbox)}
                disabled={isBlocked}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isBlocked
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                    : theme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                title={isBlocked ? 'Conta bloqueada - Templates não disponíveis' : 'Gerenciar templates'}
              >
                📄 Templates
              </button>
              <button
                onClick={(e) => {
                  if (!isBlocked) {
                    e.stopPropagation();
                    setProfileInbox(inbox);
                  }
                }}
                disabled={isBlocked}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isBlocked
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                    : theme === 'dark'
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
                title={isBlocked ? 'Conta bloqueada - Configuração não disponível' : 'Configurar perfil'}
              >
                ⚙️ Configurar
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {/* Footer com contagem */}
      <div className={`mt-6 text-center text-sm ${
        theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
      }`}>
        Total: {inboxes.length} caixa{inboxes.length !== 1 ? 's' : ''} de entrada
      </div>

      {/* Modal de Configuração de Perfil */}
      {profileInbox && (
        <BusinessProfileModal
          inbox={profileInbox}
          onClose={() => setProfileInbox(null)}
        />
      )}
    </div>
  );
}

